import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Check, ChevronRight, ChevronLeft, Truck, CheckCircle,
  Camera, Image as ImageIcon, X, Loader, Clock, AlertCircle, Info
} from 'lucide-react';
import api from '../utils/api';
import { useSettings } from '../context/SettingsContext';
import { resizeImage } from '../utils/imageUtils';

const inputCls = "w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-apex-blue-500 focus:ring-1 focus:ring-apex-blue-500";
const SERVICES = ['Free Car Removal','Cash for Cars','Standard Towing','Emergency Towing','Heavy Machinery Transport','Long Distance Transport'];

// All possible 1-hour time slots 6am – 8pm
const ALL_SLOTS = ['06:00','07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00','20:00'];
const SLOT_PERIODS = [
  { label:'Morning',   slots:['06:00','07:00','08:00','09:00','10:00','11:00'] },
  { label:'Afternoon', slots:['12:00','13:00','14:00','15:00','16:00']         },
  { label:'Evening',   slots:['17:00','18:00','19:00','20:00']                 },
];

function fmtSlot(t) {
  const [h, m] = t.split(':').map(Number);
  return `${h > 12 ? h-12 : h === 0 ? 12 : h}:${m.toString().padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

// ── Time Slot Grid ────────────────────────────────────────────────────────
function TimeSlotGrid({ selectedDate, selectedTime, onSelect, bookedSlots, slotsLoading }) {
  const [tappedBooked, setTappedBooked] = useState(null);

  const getSuggestions = (tappedTime) => {
    const idx    = ALL_SLOTS.indexOf(tappedTime);
    const before = [...ALL_SLOTS.slice(0, idx)].reverse().find(t => !bookedSlots.includes(t));
    const after  = ALL_SLOTS.slice(idx + 1).filter(t => !bookedSlots.includes(t)).slice(0, 2);
    return [before, ...after].filter(Boolean);
  };

  if (!selectedDate) return (
    <div className="text-center py-6 border-2 border-dashed border-gray-200 rounded-xl text-gray-400 text-sm">
      <Clock className="w-8 h-8 mx-auto mb-2 text-gray-200" />
      Select a date first to see available times
    </div>
  );

  if (slotsLoading) return (
    <div className="text-center py-6 text-gray-400">
      <Loader className="w-5 h-5 animate-spin mx-auto mb-2" />
      Checking availability...
    </div>
  );

  const suggestions = tappedBooked ? getSuggestions(tappedBooked) : [];

  return (
    <div>
      {SLOT_PERIODS.map(({ label, slots }) => (
        <div key={label} className="mb-4">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">{label}</p>
          <div className="grid grid-cols-4 gap-1.5">
            {slots.map(t => {
              const booked   = bookedSlots.includes(t);
              const selected = selectedTime === t;
              return (
                <button key={t}
                  onClick={() => {
                    if (booked) { setTappedBooked(t === tappedBooked ? null : t); }
                    else { onSelect(t); setTappedBooked(null); }
                  }}
                  className={`py-2.5 rounded-xl text-xs font-bold border-2 transition-all leading-tight
                    ${selected  ? 'bg-apex-blue-700 border-apex-blue-700 text-white shadow-md'
                    : booked    ? 'bg-red-50 border-red-100 text-red-300 cursor-pointer'
                                : 'bg-white border-gray-200 text-gray-700 hover:border-apex-blue-300 hover:bg-apex-blue-50'}`}>
                  {fmtSlot(t)}
                  {booked && <span className="block text-[9px] mt-0.5 opacity-70">Taken</span>}
                  {selected && <span className="block text-[9px] mt-0.5 opacity-70">Selected ✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Suggestion panel when a booked slot is tapped */}
      {tappedBooked && suggestions.length > 0 && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 mt-2">
          <div className="flex items-start gap-2 mb-3">
            <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-800 font-bold text-sm">{fmtSlot(tappedBooked)} is already booked</p>
              <p className="text-amber-700 text-xs mt-0.5">Here are the nearest available times:</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {suggestions.map(s => (
              <button key={s} onClick={() => { onSelect(s); setTappedBooked(null); }}
                className="px-4 py-2 bg-apex-blue-700 text-white font-bold text-sm rounded-xl hover:bg-apex-blue-600 transition-all">
                {fmtSlot(s)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* If completely full */}
      {ALL_SLOTS.every(t => bookedSlots.includes(t)) && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 text-center">
          <p className="text-red-700 font-bold text-sm">This day is fully booked</p>
          <p className="text-red-600 text-xs mt-1">Please choose a different date or call us directly</p>
        </div>
      )}
    </div>
  );
}

// ── Main BookingPage ──────────────────────────────────────────────────────
export default function BookingPage() {
  const { settings, telHref } = useSettings();
  const [step, setStep]         = useState(1);
  const [done, setDone]         = useState(false);
  const [refId, setRefId]       = useState('');
  const [photos, setPhotos]     = useState([]);
  const [uploading, setUploading] = useState(false);
  const [prefillData, setPrefillData] = useState(null); // from estimate page
  const [bookedSlots, setBookedSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const cameraRef  = useRef(null);
  const galleryRef = useRef(null);

  const [form, setForm] = useState({
    service_type:'', customer_name:'', customer_phone:'', customer_email:'',
    pickup_address:'', pickup_suburb:'', dropoff_address:'', dropoff_suburb:'',
    scheduled_date:'', scheduled_time:'', vehicle_make:'', vehicle_model:'',
    vehicle_year:'', vehicle_color:'', vehicle_rego:'', vehicle_condition:'',
    notes:'', charge:'',
  });
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const needsDrop = !['Free Car Removal','Cash for Cars'].includes(form.service_type);

  // ── Read sessionStorage prefill from estimate page ───────────────────────
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('apex_booking_prefill');
      if (!raw) return;
      const p = JSON.parse(raw);
      sessionStorage.removeItem('apex_booking_prefill');
      setPrefillData(p);

      // Apply all prefill fields to form
      setForm(f => ({
        ...f,
        service_type:      p.service_type      || '',
        customer_name:     p.customer_name     || '',
        customer_phone:    p.customer_phone    || '',
        customer_email:    p.customer_email    || '',
        pickup_address:    p.pickup_address    || '',
        pickup_suburb:     p.pickup_suburb     || '',
        dropoff_address:   p.dropoff_address   || '',
        dropoff_suburb:    p.dropoff_suburb    || '',
        vehicle_make:      p.vehicle_make      || '',
        vehicle_model:     p.vehicle_model     || '',
        vehicle_year:      p.vehicle_year      || '',
        vehicle_rego:      p.vehicle_rego      || '',
        vehicle_condition: p.vehicle_condition || '',
        charge:            p.est_min ? String(p.est_min) : '',
      }));

      // If came from estimate → ALWAYS jump to step 4
      // Customer already filled everything on estimate page
      if (p.from === 'estimate') {
        setStep(4);
      }

      // Fetch photos from the estimate record (photos not stored in sessionStorage)
      if (p.estimate_id) {
        api.get(`/api/estimates/${p.estimate_id}`)
          .then(res => {
            const imgs = res.data?.images;
            if (Array.isArray(imgs) && imgs.length > 0) {
              setPhotos(imgs);
            }
          })
          .catch(() => {});
      }
    } catch {}
  }, []);

  // ── Fetch booked slots when date changes ─────────────────────────────────
  const fetchBookedSlots = useCallback(async (date) => {
    if (!date) return;
    setSlotsLoading(true);
    try {
      const res = await api.get('/api/bookings');
      const booked = res.data
        .filter(b => b.scheduled_date === date && b.status !== 'cancelled')
        .map(b => b.scheduled_time?.slice(0, 5))
        .filter(Boolean);
      setBookedSlots(booked);
    } catch { setBookedSlots([]); }
    finally { setSlotsLoading(false); }
  }, []);

  useEffect(() => {
    if (form.scheduled_date) fetchBookedSlots(form.scheduled_date);
  }, [form.scheduled_date, fetchBookedSlots]);

  // ── Photo upload ─────────────────────────────────────────────────────────
  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const newPhotos = await Promise.all(files.map(f => resizeImage(f)));
      setPhotos(prev => [...prev, ...newPhotos]);
    } catch { alert('Photo upload failed.'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const submit = async () => {
    try {
      const res = await api.post('/api/bookings', {
        ...form,
        charge:       Number(form.charge) || 0,
        estimate_id:  prefillData?.estimate_id || '',
        booking_type: 'customer',
        images: {
          customer: photos.map((url, i) => ({ id:String(Date.now()+i), url, added: new Date().toISOString().split('T')[0] })),
          start: [], complete: [],
        },
      });
      // If came from estimate, mark it as converted
      if (prefillData?.estimate_id) {
        api.put(`/api/estimates/${prefillData.estimate_id}`, { converted: true, status: 'converted' }).catch(() => {});
      }
      setRefId(res.data.id);
      setDone(true);
    } catch { alert(`Submission failed. Please call us on ${settings.company_phone}`); }
  };

  const steps = ['Service','Your Details','Location','Schedule'];

  // ── Success screen ────────────────────────────────────────────────────────
  if (done) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-black text-apex-blue-700 mb-2">Booking Received!</h2>
        <p className="text-gray-500 mb-1">Reference: <span className="font-bold text-gray-800">{refId?.slice(-8)}</span></p>
        <p className="text-gray-500 text-sm mb-1">We'll call <strong>{form.customer_phone}</strong> shortly to confirm.</p>
        {form.scheduled_date && (
          <p className="text-apex-blue-700 font-bold text-sm mb-6">
            📅 {form.scheduled_date} at {fmtSlot(form.scheduled_time || '09:00')}
          </p>
        )}
        <Link to="/" className="px-6 py-3 bg-apex-blue-700 text-white font-bold rounded-xl hover:bg-apex-blue-600">Back to Home</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <input ref={cameraRef}  type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={handleFile} />
      <input ref={galleryRef} type="file" accept="image/*"                       multiple className="hidden" onChange={handleFile} />

      {/* Header */}
      <div className="bg-apex-blue-700 text-white px-4 py-5">
        <div className="max-w-xl mx-auto">
          <Link to="/" className="flex items-center gap-2 text-white/70 text-sm mb-4 hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-apex-yellow-500 rounded-xl flex items-center justify-center">
              <Truck className="w-5 h-5 text-apex-blue-700" />
            </div>
            <div>
              <h1 className="font-black text-xl">Book Your Service</h1>
              <p className="text-white/60 text-xs">
                {prefillData ? 'Pre-filled from your estimate — just pick a time' : 'All Brisbane suburbs · Confirmed within 30 minutes'}
              </p>
            </div>
          </div>

          {/* Step indicators */}
          <div className="flex items-center">
            {steps.map((s, i) => {
              const isDone = i + 1 < step, isAct = i + 1 === step;
              return (
                <React.Fragment key={s}>
                  <div className="flex flex-col items-center">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black
                      ${isDone ? 'bg-green-400 text-apex-blue-700' : isAct ? 'bg-apex-yellow-500 text-apex-blue-700' : 'bg-white/20 text-white/50'}`}>
                      {isDone ? '✓' : i+1}
                    </div>
                    <span className={`text-[9px] mt-1 hidden md:block ${isAct ? 'text-white font-bold' : 'text-white/40'}`}>{s}</span>
                  </div>
                  {i < 3 && <div className={`flex-1 h-0.5 mx-2 ${isDone ? 'bg-green-400' : 'bg-white/20'}`} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 py-6">

        {/* Prefill banner */}
        {prefillData && (
          <div className="bg-green-50 border-2 border-green-300 rounded-2xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xl">✅</span>
              <p className="text-green-800 font-black text-sm">All details pre-filled from your estimate</p>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-green-700 mb-2">
              {prefillData.service_type && <span>📋 <strong>Service:</strong> {prefillData.service_type}</span>}
              {prefillData.customer_name && <span>👤 <strong>Name:</strong> {prefillData.customer_name}</span>}
              {prefillData.customer_phone && <span>📞 <strong>Phone:</strong> {prefillData.customer_phone}</span>}
              {prefillData.pickup_address && <span>📍 <strong>Pickup:</strong> {prefillData.pickup_address}</span>}
              {prefillData.dropoff_address && <span>🏁 <strong>Drop-off:</strong> {prefillData.dropoff_address}</span>}
            </div>
            {(prefillData.est_min || 0) > 0 && (
              <p className="text-green-800 font-black text-sm">
                💰 Estimate: ${prefillData.est_min?.toLocaleString()} – ${prefillData.est_max?.toLocaleString()}
                <span className="font-normal text-green-600 text-xs ml-2">(guide only — final confirmed on arrival)</span>
              </p>
            )}
          </div>
        )}

        <div className="bg-white rounded-2xl border border-gray-200 p-6">

          {/* ── Step 1 — Service ── */}
          {step === 1 && (
            <div>
              <h2 className="font-black text-gray-900 text-lg mb-4">Select Service</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-6">
                {SERVICES.map(s => (
                  <button key={s} onClick={() => setForm(f => ({ ...f, service_type: s }))}
                    className={`p-4 rounded-xl border-2 text-left font-semibold text-sm transition-all
                      ${form.service_type===s ? 'border-apex-blue-500 bg-apex-blue-50 text-apex-blue-700' : 'border-gray-200 hover:border-gray-300 text-gray-700'}`}>
                    {s}
                  </button>
                ))}
              </div>
              <button onClick={() => form.service_type && setStep(2)}
                className={`w-full py-3.5 rounded-xl font-black flex items-center justify-center gap-2 text-base
                  ${form.service_type ? 'bg-apex-blue-700 text-white hover:bg-apex-blue-600' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                Next <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* ── Step 2 — Details ── */}
          {step === 2 && (
            <div className="space-y-4">
              <h2 className="font-black text-gray-900 text-lg">Your Details</h2>
              {prefillData && form.customer_name && (
                <div className="bg-green-50 border border-green-200 rounded-xl px-3 py-2 text-xs text-green-700 font-medium">
                  ✓ Pre-filled from estimate — update if needed
                </div>
              )}
              <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Full Name *</span>
                <input value={form.customer_name} onChange={set('customer_name')} className={`${inputCls} mt-1.5`} placeholder="John Smith" /></label>
              <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Phone Number *</span>
                <input value={form.customer_phone} onChange={set('customer_phone')} className={`${inputCls} mt-1.5`} placeholder="07 0000 0000" type="tel" /></label>
              <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Email (optional)</span>
                <input value={form.customer_email} onChange={set('customer_email')} className={`${inputCls} mt-1.5`} placeholder="john@email.com" type="email" /></label>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setStep(1)} className="px-4 py-3.5 border-2 border-gray-200 rounded-xl font-bold text-gray-500"><ChevronLeft className="w-5 h-5" /></button>
                <button onClick={() => form.customer_name && form.customer_phone && setStep(3)}
                  className={`flex-1 py-3.5 rounded-xl font-black flex items-center justify-center gap-2 ${form.customer_name && form.customer_phone ? 'bg-apex-blue-700 text-white hover:bg-apex-blue-600' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                  Next <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* ── Step 3 — Location ── */}
          {step === 3 && (
            <div className="space-y-4">
              <h2 className="font-black text-gray-900 text-lg">Locations</h2>
              {prefillData && form.pickup_address && (
                <div className="bg-green-50 border border-green-200 rounded-xl px-3 py-2 text-xs text-green-700 font-medium">
                  ✓ Pre-filled from your estimate — update if needed
                </div>
              )}
              <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Pickup Address *</span>
                <input value={form.pickup_address} onChange={set('pickup_address')} className={`${inputCls} mt-1.5`} placeholder="123 Main St" /></label>
              <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Pickup Suburb / Postcode</span>
                <input value={form.pickup_suburb} onChange={set('pickup_suburb')} className={`${inputCls} mt-1.5`} placeholder="Fortitude Valley QLD 4006" /></label>
              {needsDrop && (<>
                <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Drop-off Address</span>
                  <input value={form.dropoff_address} onChange={set('dropoff_address')} className={`${inputCls} mt-1.5`} /></label>
                <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Drop-off Suburb / Postcode</span>
                  <input value={form.dropoff_suburb} onChange={set('dropoff_suburb')} className={`${inputCls} mt-1.5`} /></label>
              </>)}
              <div className="flex gap-3 pt-2">
                <button onClick={() => setStep(2)} className="px-4 py-3.5 border-2 border-gray-200 rounded-xl font-bold text-gray-500"><ChevronLeft className="w-5 h-5" /></button>
                <button onClick={() => form.pickup_address && setStep(4)}
                  className={`flex-1 py-3.5 rounded-xl font-black flex items-center justify-center gap-2 ${form.pickup_address ? 'bg-apex-blue-700 text-white hover:bg-apex-blue-600' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                  Next <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* ── Step 4 — Schedule ── */}
          {step === 4 && (
            <div className="space-y-5">
              <h2 className="font-black text-gray-900 text-lg">Pick a Date & Time</h2>

              {/* Date picker */}
              <label className="block">
                <span className="text-xs font-bold text-gray-500 uppercase">Preferred Date *</span>
                <input type="date" value={form.scheduled_date}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={e => { set('scheduled_date')(e); setForm(f=>({...f,scheduled_time:''})); }}
                  className={`${inputCls} mt-1.5`} />
              </label>

              {/* Time slot grid */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Available Times</span>
                  {bookedSlots.length > 0 && (
                    <span className="text-xs text-gray-400">{ALL_SLOTS.length - bookedSlots.length} of {ALL_SLOTS.length} slots free</span>
                  )}
                </div>
                <TimeSlotGrid
                  selectedDate={form.scheduled_date}
                  selectedTime={form.scheduled_time}
                  onSelect={v => setForm(f => ({ ...f, scheduled_time: v }))}
                  bookedSlots={bookedSlots}
                  slotsLoading={slotsLoading}
                />
              </div>

              {/* Selected time confirmation */}
              {form.scheduled_time && (
                <div className="bg-apex-blue-50 border-2 border-apex-blue-200 rounded-xl px-4 py-3 flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-apex-blue-600 shrink-0" />
                  <div>
                    <p className="text-apex-blue-700 font-bold text-sm">
                      {form.scheduled_date} at {fmtSlot(form.scheduled_time)}
                    </p>
                    <p className="text-apex-blue-500 text-xs">We'll confirm this within 30 minutes of booking</p>
                  </div>
                </div>
              )}

              {/* Vehicle details (only if not prefilled) */}
              {/* Vehicle details — always editable */}
              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Vehicle Details (optional)</p>
                {prefillData && form.vehicle_make && (
                  <div className="bg-green-50 border border-green-200 rounded-xl px-3 py-2 text-xs text-green-700 font-medium mb-3">
                    ✓ Pre-filled from estimate — update if needed
                  </div>
                )}
                <div className="grid grid-cols-3 gap-3">
                  <label className="block"><span className="text-xs font-bold text-gray-400 uppercase">Make</span>
                    <input value={form.vehicle_make} onChange={set('vehicle_make')} className={`${inputCls} mt-1`} placeholder="Toyota" /></label>
                  <label className="block"><span className="text-xs font-bold text-gray-400 uppercase">Model</span>
                    <input value={form.vehicle_model} onChange={set('vehicle_model')} className={`${inputCls} mt-1`} placeholder="Corolla" /></label>
                  <label className="block"><span className="text-xs font-bold text-gray-400 uppercase">Year</span>
                    <input value={form.vehicle_year} onChange={set('vehicle_year')} className={`${inputCls} mt-1`} placeholder="2015" /></label>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <label className="block"><span className="text-xs font-bold text-gray-400 uppercase">Rego</span>
                    <input value={form.vehicle_rego} onChange={set('vehicle_rego')} className={`${inputCls} mt-1`} placeholder="123 ABC" /></label>
                  <label className="block"><span className="text-xs font-bold text-gray-400 uppercase">Condition</span>
                    <select value={form.vehicle_condition} onChange={set('vehicle_condition')} className={`${inputCls} mt-1 bg-white`}>
                      <option value="">Select</option>
                      <option value="running">Running / Driveable</option>
                      <option value="not_running">Not Running</option>
                      <option value="accident_damaged">Accident Damaged</option>
                    </select>
                  </label>
                </div>
              </div>

              <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Notes</span>
                <textarea value={form.notes} onChange={set('notes')} className={`${inputCls} mt-1.5 resize-none`} rows={2}
                  placeholder="Access notes, gate codes, anything we should know..." /></label>

              {/* Photo upload */}
              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
                  Vehicle Photos {prefillData && photos.length > 0 ? `(${photos.length} from estimate)` : '(optional)'}
                </p>
                {photos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    {photos.map((src, i) => (
                      <div key={i} className="relative aspect-[4/3] rounded-xl overflow-hidden bg-gray-100">
                        <img src={src} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => setPhotos(p => p.filter((_,idx) => idx !== i))}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
                {uploading ? (
                  <div className="flex items-center gap-2 text-sm text-apex-blue-600 py-2"><Loader className="w-4 h-4 animate-spin" /> Uploading...</div>
                ) : (
                  <div className="flex gap-2">
                    <button type="button" onClick={() => cameraRef.current?.click()}
                      className="flex-1 py-2.5 border-2 border-dashed border-apex-blue-200 rounded-xl text-xs font-bold text-apex-blue-600 hover:bg-apex-blue-50 flex items-center justify-center gap-1.5">
                      <Camera className="w-4 h-4" /> Take Photo
                    </button>
                    <button type="button" onClick={() => galleryRef.current?.click()}
                      className="flex-1 py-2.5 border-2 border-dashed border-gray-200 rounded-xl text-xs font-bold text-gray-500 hover:border-apex-blue-200 hover:text-apex-blue-500 flex items-center justify-center gap-1.5">
                      <ImageIcon className="w-4 h-4" /> Choose Files
                    </button>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={() => setStep(prefillData ? 1 : 3)}
                  className="px-4 py-3.5 border-2 border-gray-200 rounded-xl font-bold text-gray-500">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button onClick={submit}
                  disabled={!form.scheduled_date || !form.scheduled_time}
                  className={`flex-1 py-3.5 rounded-xl font-black flex items-center justify-center gap-2 text-base transition-all
                    ${form.scheduled_date && form.scheduled_time
                      ? 'bg-apex-yellow-500 text-apex-blue-700 hover:bg-apex-yellow-400'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                  <Check className="w-5 h-5" />
                  {form.scheduled_time ? `Confirm — ${fmtSlot(form.scheduled_time)}` : 'Select a time slot'}
                </button>
              </div>

              <p className="text-center text-xs text-gray-400">
                Or call us: <a href={telHref()} className="font-bold text-apex-blue-700">{settings.company_phone}</a>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}